// src/components/ErrorBoundary.jsx
import React from "react";
import * as Sentry from "@sentry/react";
import { useRouteError, useNavigate, isRouteErrorResponse } from "react-router-dom";

// ── Generic inline fallback (used inside pages / widget areas) ──────────────
export const ErrorFallback = ({ error, resetError }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      padding: "2rem",
      textAlign: "center",
      border: "1px solid #fee2e2",
      borderRadius: "8px",
      backgroundColor: "#fff5f5",
      margin: "1rem",
    }}
  >
    <h2 style={{ color: "#dc2626", marginBottom: "0.5rem" }}>
      Something went wrong
    </h2>
    <p style={{ color: "#6b7280", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
      {error?.message || "An unexpected error occurred"}
    </p>
    <button
      onClick={resetError}
      style={{
        padding: "0.5rem 1.5rem",
        backgroundColor: "#dc2626",
        color: "white",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "0.9rem",
      }}
    >
      Try Again
    </button>
  </div>
);

// ── Full-page fallback for React Router errorElement ────────────────────────
export const RouteErrorFallback = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const is404 =
    isRouteErrorResponse(error) && error.status === 404;

  const message = isRouteErrorResponse(error)
    ? `${error.status} – ${error.statusText || error.data}`
    : error?.message || "An unexpected error occurred";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "2rem",
        textAlign: "center",
        backgroundColor: "#fff5f5",
      }}
    >
      <h1 style={{ color: "#dc2626", fontSize: "2rem", marginBottom: "0.5rem" }}>
        {is404 ? "Page Not Found" : "Something went wrong"}
      </h1>
      <p style={{ color: "#6b7280", marginBottom: "2rem", maxWidth: "400px" }}>
        {message}
      </p>
      <div style={{ display: "flex", gap: "1rem" }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "0.5rem 1.5rem",
            backgroundColor: "#6b7280",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Go Back
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.5rem 1.5rem",
            backgroundColor: "#dc2626",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Reload Page
        </button>
      </div>
    </div>
  );
};

// ── Sentry-backed ErrorBoundary (wraps any subtree) ─────────────────────────
const ErrorBoundary = ({ children, fallback }) => (
  <Sentry.ErrorBoundary
    fallback={fallback || ErrorFallback}
    onError={(error, componentStack) => {
      Sentry.withScope((scope) => {
        scope.setTag("error.type", "react_boundary");
        scope.setExtra("componentStack", componentStack);
        Sentry.captureException(error);
      });
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;